import { z } from 'zod';
import { Line, type Script } from './domain.js';

/**
 * Edits to a script are small, named operations. The editor, the chat assistant and undo all use them.
 * `applyOps` is pure: it returns the new script, the inverse ops (for undo) and the changed field keys
 * (for highlighting). Every successful apply bumps `version` by one.
 */

export const ScriptOp = z.discriminatedUnion('op', [
  // `expected`: the value the author saw. If the field holds something else now, the op conflicts
  // instead of silently overwriting a newer human correction (review F08).
  z.object({
    op: z.literal('setText'),
    lineId: z.string(),
    value: z.string().max(600),
    expected: z.string().optional(),
  }),
  z.object({
    op: z.literal('setOnScreen'),
    lineId: z.string(),
    value: z.string().max(200),
    expected: z.string().optional(),
  }),
  z.object({
    op: z.literal('setVisual'),
    lineId: z.string(),
    value: z.string().max(400),
    expected: z.string().optional(),
  }),
  z.object({
    op: z.literal('setBeat'),
    lineId: z.string(),
    value: z.string().max(40),
    expected: z.string().optional(),
  }),
  z.object({ op: z.literal('insertLine'), index: z.number().int().nonnegative(), line: Line }),
  // `expected`: digest of the whole line the author saw (lineDigest). Removing a line someone edited
  // meanwhile conflicts instead of deleting the newer work (recheck R06).
  z.object({ op: z.literal('removeLine'), lineId: z.string(), expected: z.string().optional() }),
  z.object({ op: z.literal('moveLine'), lineId: z.string(), toIndex: z.number().int().nonnegative() }),
  z.object({ op: z.literal('setTitle'), value: z.string().max(200) }),
]);
export type ScriptOp = z.infer<typeof ScriptOp>;

export class ScriptOpError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScriptOpError';
  }
}

/** An op's `expected` value no longer matches: someone changed the same field meanwhile. */
export class ScriptOpConflict extends ScriptOpError {
  constructor(readonly fields: string[]) {
    super(`Changed meanwhile: ${fields.join(', ')}`);
    this.name = 'ScriptOpConflict';
  }
}

export interface ApplyResult {
  script: Script;
  inverse: ScriptOp[];
  /** Field keys like "text:l1", "onScreen:l1", "line:l3" (whole line inserted or moved). */
  changed: string[];
}

const FIELD = { setText: 'text', setOnScreen: 'onScreen', setVisual: 'visual', setBeat: 'beat' } as const;

const MIN_LINES = 1;
const MAX_LINES = 40;

function indexOf(lines: Line[], lineId: string): number {
  const i = lines.findIndex((l) => l.id === lineId);
  if (i < 0) throw new ScriptOpError(`Line ${lineId} does not exist`);
  return i;
}

export function applyOps(script: Script, ops: readonly ScriptOp[]): ApplyResult {
  if (ops.length === 0) throw new ScriptOpError('No changes');
  let lines = script.lines.map((l) => ({ ...l, fx: l.fx.map((f) => ({ ...f })) }));
  let title = script.title;
  const inverse: ScriptOp[] = [];
  const changed = new Set<string>();

  for (const raw of ops) {
    const op = ScriptOp.parse(raw);
    switch (op.op) {
      case 'setText':
      case 'setOnScreen':
      case 'setVisual':
      case 'setBeat': {
        const field = FIELD[op.op];
        const i = indexOf(lines, op.lineId);
        const line = lines[i]!;
        if (op.expected !== undefined && line[field] !== op.expected)
          throw new ScriptOpConflict([`${field}:${op.lineId}`]);
        if (line[field] === op.value) break;
        inverse.unshift({ op: op.op, lineId: op.lineId, value: line[field], expected: op.value });
        lines[i] = { ...line, [field]: op.value };
        changed.add(`${field}:${op.lineId}`);
        break;
      }
      case 'insertLine': {
        if (lines.length >= MAX_LINES) throw new ScriptOpError(`A script has at most ${MAX_LINES} lines`);
        if (lines.some((l) => l.id === op.line.id)) throw new ScriptOpError(`Line ${op.line.id} already exists`);
        const index = Math.min(op.index, lines.length);
        lines = [...lines.slice(0, index), op.line, ...lines.slice(index)];
        inverse.unshift({ op: 'removeLine', lineId: op.line.id, expected: lineDigest(op.line) });
        changed.add(`line:${op.line.id}`);
        break;
      }
      case 'removeLine': {
        if (lines.length <= MIN_LINES) throw new ScriptOpError('A script needs at least one line');
        const i = indexOf(lines, op.lineId);
        if (op.expected !== undefined && lineDigest(lines[i]!) !== op.expected)
          throw new ScriptOpConflict([`line:${op.lineId}`]);
        const [gone] = lines.splice(i, 1);
        inverse.unshift({ op: 'insertLine', index: i, line: gone! });
        break;
      }
      case 'moveLine': {
        const from = indexOf(lines, op.lineId);
        const to = Math.min(op.toIndex, lines.length - 1);
        if (from === to) break;
        const [line] = lines.splice(from, 1);
        lines.splice(to, 0, line!);
        inverse.unshift({ op: 'moveLine', lineId: op.lineId, toIndex: from });
        changed.add(`line:${op.lineId}`);
        break;
      }
      case 'setTitle': {
        if (title === op.value) break;
        inverse.unshift({ op: 'setTitle', value: title });
        title = op.value;
        changed.add('title');
        break;
      }
    }
  }

  if (inverse.length === 0) throw new ScriptOpError('No changes');
  return { script: { ...script, title, lines, version: script.version + 1 }, inverse, changed: [...changed] };
}

/** Short German-free label for history entries, e.g. "Edited hook spoken, beat 3 on screen". */
export function describeOps(script: Script, ops: readonly ScriptOp[]): string {
  const name = (lineId: string) => {
    const i = script.lines.findIndex((l) => l.id === lineId);
    return i === 0 ? 'hook' : i > 0 ? `line ${i + 1}` : 'a line';
  };
  const parts = ops.map((op) => {
    switch (op.op) {
      case 'setText':
        return `${name(op.lineId)} spoken`;
      case 'setOnScreen':
        return `${name(op.lineId)} on screen`;
      case 'setVisual':
        return `${name(op.lineId)} visual`;
      case 'setBeat':
        return `${name(op.lineId)} beat`;
      case 'insertLine':
        return `added line ${op.index + 1}`;
      case 'removeLine':
        return `removed ${name(op.lineId)}`;
      case 'moveLine':
        return `moved ${name(op.lineId)} to line ${op.toIndex + 1}`;
      case 'setTitle':
        return 'title';
    }
  });
  return `Edited ${[...new Set(parts)].join(', ')}`;
}

/** Stable digest of a whole line (all fields), for removeLine expectations. */
export function lineDigest(line: Script['lines'][number]): string {
  const canon = (v: unknown): unknown =>
    Array.isArray(v)
      ? v.map(canon)
      : v && typeof v === 'object'
        ? Object.keys(v as object)
            .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
            .sort()
            .map((k) => [k, canon((v as Record<string, unknown>)[k])])
        : v;
  return JSON.stringify(canon(line));
}

/** Adds `expected` (current value or line digest) to field and remove ops, so they are safe to apply later. */
export function withExpectations(script: Script, ops: readonly ScriptOp[]): ScriptOp[] {
  return ops.map((op) => {
    if (op.op === 'setText' || op.op === 'setOnScreen' || op.op === 'setVisual' || op.op === 'setBeat') {
      const line = script.lines.find((l) => l.id === op.lineId);
      return line && op.expected === undefined ? { ...op, expected: line[FIELD[op.op]] } : op;
    }
    if (op.op === 'removeLine' && op.expected === undefined) {
      const line = script.lines.find((l) => l.id === op.lineId);
      return line ? { ...op, expected: lineDigest(line) } : op;
    }
    return op;
  });
}

export interface Rebase {
  /** Ops that touch only fields nobody changed since `base`. */
  applicable: ScriptOp[];
  /** Ops on fields (or lines) that changed since `base`: returned to the person as a proposal. */
  conflicting: ScriptOp[];
  conflictFields: string[];
}

/**
 * Three-way rebase of `ops`, written against `base`, onto `current`. Independent fields merge; any op on
 * a field that changed meanwhile, or on a line that was removed, conflicts. Structural ops (insert,
 * remove, move) conflict when the line set or order changed. Nothing is ever silently overwritten.
 */
export function rebaseOps(base: Script, current: Script, ops: readonly ScriptOp[]): Rebase {
  const applicable: ScriptOp[] = [];
  const conflicting: ScriptOp[] = [];
  const conflictFields = new Set<string>();
  const baseById = new Map(base.lines.map((l) => [l.id, l]));
  const curById = new Map(current.lines.map((l) => [l.id, l]));
  const sameStructure = base.lines.map((l) => l.id).join() === current.lines.map((l) => l.id).join();
  for (const op of ops) {
    switch (op.op) {
      case 'setText':
      case 'setOnScreen':
      case 'setVisual':
      case 'setBeat': {
        const f = FIELD[op.op];
        const b = baseById.get(op.lineId);
        const c = curById.get(op.lineId);
        const key = `${f}:${op.lineId}`;
        if (!b || !c || b[f] !== c[f]) {
          conflicting.push(op);
          conflictFields.add(key);
        } else applicable.push({ ...op, expected: c[f] });
        break;
      }
      case 'setTitle':
        if (base.title !== current.title) {
          conflicting.push(op);
          conflictFields.add('title');
        } else applicable.push(op);
        break;
      case 'removeLine': {
        // Removing a line conflicts when the line itself changed, even if the order did not.
        const b = baseById.get(op.lineId);
        const c = curById.get(op.lineId);
        if (!sameStructure || !b || !c || lineDigest(b) !== lineDigest(c)) {
          conflicting.push(op);
          conflictFields.add(`line:${op.lineId}`);
        } else applicable.push({ ...op, expected: lineDigest(c) });
        break;
      }
      case 'moveLine': {
        // A move conflicts when the order changed or the moved line itself changed.
        const b = baseById.get(op.lineId);
        const c = curById.get(op.lineId);
        if (!sameStructure || !b || !c || lineDigest(b) !== lineDigest(c)) {
          conflicting.push(op);
          conflictFields.add(`line:${op.lineId}`);
        } else applicable.push(op);
        break;
      }
      default:
        // insertLine: conflicts when the neighbourhood (the line order) changed.
        if (sameStructure) applicable.push(op);
        else {
          conflicting.push(op);
          conflictFields.add('lines');
        }
    }
  }
  return { applicable, conflicting, conflictFields: [...conflictFields] };
}
